import React from 'react';

import Button from 'components/DesignSystem/Button';
import { formatUpdatedAge } from 'helper/flux/myStudiesAutoRefresh';
import useNow from 'utils/hooks/useNow';

import './index.scss';

/** Tique do relógio da idade ("atualizado há N s"). */
const TICK_MS = 1000;

/** Cor da bolinha: verde com estudo ativo, cinza sem ativo, vermelha em erro (.dc.html:723). */
export type AutoRefreshStatus = 'active' | 'idle' | 'error';

export interface AutoRefreshIndicatorProps {
  /**
   * Fim da última atualização do que a lista mostra, em ms: o `updatedAt` do
   * `useMyStudiesAutoRefresh` (D03, `max(fulfilledTimeStamp da lista, último status)` da página;
   * no pending, a hora da lista que continua na tela). Sem ele (sem lista na tela), a bolinha e a
   * idade não aparecem; fica só o botão. Cada hora nova realinha o relógio.
   */
  lastUpdatedAt?: number;
  /** A página tem estudo ativo (`polling.hasActive`): bolinha verde e "Atualização automática". */
  hasActive: boolean;
  /** A lista ou o status falhou: bolinha vermelha e "Sem conexão", acima de `hasActive`. */
  hasError: boolean;
  /** Refetch manual em andamento (`manualRefreshPending`): o botão fica `aria-disabled`. */
  refreshing: boolean;
  /** "Atualizar": refaz a página atual (`refetchPage` da #1999). */
  onRefresh: () => void;
}

/**
 * Indicador da atualização automática da aba Meus Estudos (#2004, nota 4 e `.dc.html:233-236`),
 * no slot `updateIndicator` do resumo (#1997): bolinha de 8 px, texto e o botão "Atualizar".
 *
 * - Com estudo ativo: bolinha verde e "Atualização automática · atualizado há N s".
 * - Sem ativo: bolinha cinza e "atualizado há N s".
 * - Em erro: bolinha vermelha e "Sem conexão" (`.dc.html:723-724`).
 *
 * A idade segue a D18 (`formatUpdatedAge`, `helper/flux/myStudiesAutoRefresh`).
 *
 * O relógio (`useNow`, 1 s) mora aqui dentro, e só este componente re-renderiza a cada tique: a
 * casca e as linhas ficam como estão. Fica desligado quando não há o que contar (erro ou sem
 * hora). Quando `lastUpdatedAt` muda (resposta da lista ou retrato de status), o relógio é
 * relido e realinhado a ela: N volta a 0 na hora e sobe 1 a cada segundo dali em diante.
 *
 * Acessibilidade: a idade NÃO fica em região viva, para o leitor de tela não a anunciar a cada
 * segundo; quem a quiser lê no lugar. A bolinha é decorativa (`aria-hidden`): o texto diz o
 * estado. O erro é anunciado uma vez pela faixa (`StaleBanner`, `role="alert"`) ou pelo bloco de
 * erro da lista. O botão é o do DS: `title` "Atualizar agora", nome "Atualizar a lista" (começa
 * pelo texto visível) e, durante o refetch manual, `aria-disabled` (continua focável e ignora o
 * clique); o "Atualizando lista…" do resumo anuncia a espera.
 */
export default function AutoRefreshIndicator({
  lastUpdatedAt, hasActive, hasError, refreshing, onRefresh,
}: AutoRefreshIndicatorProps) {
  const counting = !hasError && lastUpdatedAt !== undefined;
  // Realinhado a cada hora nova: depois de uma resposta, N sobe exatamente 1 por segundo
  const now = useNow(TICK_MS, counting, lastUpdatedAt);

  let status: AutoRefreshStatus = hasActive ? 'active' : 'idle';
  let label: string | null = null;
  if (hasError) {
    status = 'error';
    label = 'Sem conexão';
  } else if (lastUpdatedAt !== undefined) {
    const prefix = hasActive ? 'Atualização automática · ' : '';
    label = `${prefix}atualizado ${formatUpdatedAge(now - lastUpdatedAt)}`;
  }

  return (
    <div className="my-studies-auto-refresh" data-testid="my-studies-auto-refresh">
      {label !== null && (
        <>
          <span
            className={`my-studies-auto-refresh-dot my-studies-auto-refresh-dot--${status}`}
            aria-hidden="true"
          />
          <span className="my-studies-auto-refresh-label">{label}</span>
        </>
      )}
      <Button
        type="secondary"
        small
        icon="refresh"
        className="my-studies-auto-refresh-button"
        title="Atualizar agora"
        aria-label="Atualizar a lista"
        aria-disabled={refreshing || undefined}
        onClick={onRefresh}
      >
        Atualizar
      </Button>
    </div>
  );
}
